import { useMemo, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";

import { buildCsv, buildCsvFilename, downloadCsv } from "../lib/csv";

/**
 * Sprint-008 A3 (design-desktop-phase-a.md §3) — the ONE shared
 * data-table component every desktop-only applied surface (Team, Admin
 * People, Admin Chores — §3.5) renders through. Never mounted below
 * `1024px` (gated by `hooks/useIsDesktop.ts` at each call site, per
 * §3.3) — the existing phone list component is that surface's
 * responsive fallback, unmodified, always.
 *
 * A11y (§5): real `<table>`/`<caption>`/`<th scope="col">` semantics,
 * `aria-sort` kept in sync with the visual glyph (CSS-only, `styles.css`),
 * keyboard-operable sort via a real `<button>` inside each sortable `<th>`
 * (native Enter/Space activation — no bespoke keydown handling needed),
 * and — when `onRowActivate` is supplied — a real `<button>` filling the
 * primary cell (never a JS-only clickable `<tr>`).
 */

export interface DataTableColumn<Row> {
  key: string;
  label: string;
  sortable?: boolean;
  /** Required when `sortable` is true. */
  getSortValue?: (row: Row) => string | number;
  /** The authoritative plain-text value for this cell — feeds CSV export
   * AND is the fallback rendered content when `render` is omitted. Must
   * never throw and must never itself be a formula-injection risk
   * concern (`lib/csv.ts` sanitizes at export time, not here). */
  getCsvValue: (row: Row) => string;
  /** Rich cell content (badges, chips, etc.). Defaults to a plain text
   * node of `getCsvValue(row)` when omitted. */
  render?: (row: Row) => ComponentChildren;
  /** Sprint-019 E5b — an optional `InfoTooltip` rendered beside the
   * column's `<th>` label (audit row #6, "Link status"). A sibling of the
   * sort button rather than nested inside it — a `<button>` can never
   * contain another focusable `<button>`. */
  headerInfo?: ComponentChildren;
}

export interface DataTableFilterChip<Row> {
  key: string;
  label: string;
  predicate: (row: Row) => boolean;
}

export interface DataTableProps<Row> {
  /** The caption's leading noun phrase, e.g. "Team" — the component
   * appends sort/filter/search/row-count state (§5's caption requirement)
   * automatically. Must never contain a zero-jargon-dictionary banned word
   * (`e2e/tests/roles/zero-jargon.spec.ts`) — this is a screen-reader-
   * facing string, so it's held to the same copy bar as visible text. */
  captionBase: string;
  columns: Array<DataTableColumn<Row>>;
  rows: Row[];
  getRowId: (row: Row) => string;
  loading?: boolean;
  loadingMessage: string;
  emptyMessage: string;

  /** Column key whose cell becomes the row's primary interactive
   * control when `onRowActivate` is supplied (§5). */
  primaryColumnKey: string;
  /** Omit entirely for a surface with no per-row action (e.g. Team —
   * see the PR description's flagged deviation). When supplied, the
   * primary cell renders as a real `<button>`. */
  onRowActivate?: (row: Row) => void;
  /** Accessible name for the primary-cell button, e.g. `Open ${name}`.
   * Required whenever `onRowActivate` is supplied. */
  getRowActivateLabel?: (row: Row) => string;

  /** Enables the search box when supplied together with `searchLabel`. */
  searchLabel?: string;
  getSearchValue?: (row: Row) => string;

  /** Enables the filter-chip group when supplied. An "All" chip
   * (matching every row) is prepended automatically — callers only
   * supply the non-"all" chips. */
  filterChips?: Array<DataTableFilterChip<Row>>;
  filterGroupLabel?: string;

  /** Caption fragment describing the table's DEFAULT (no explicit column
   * clicked yet) sort, e.g. "status ascending" — mirrors §5's caption
   * example ("sorted by status ascending"), which names only the primary
   * dimension of a compound default sort, not every tie-breaker. */
  defaultSortLabel: string;
  /** Compound comparator used until the user activates a column header. */
  defaultCompare: (a: Row, b: Row) => number;

  /** `hayloft-{screenSlug}-{YYYY-MM-DD}.csv` (§3.6). */
  screenSlug: string;
}

type SortDirection = "ascending" | "descending";
interface ActiveSort {
  key: string;
  direction: SortDirection;
}

const ALL_FILTER_KEY = "all";

export function DataTable<Row>({
  captionBase,
  columns,
  rows,
  getRowId,
  loading = false,
  loadingMessage,
  emptyMessage,
  primaryColumnKey,
  onRowActivate,
  getRowActivateLabel,
  searchLabel,
  getSearchValue,
  filterChips,
  filterGroupLabel,
  defaultSortLabel,
  defaultCompare,
  screenSlug,
}: DataTableProps<Row>) {
  const [sort, setSort] = useState<ActiveSort | null>(null);
  const [activeFilterKey, setActiveFilterKey] = useState<string>(ALL_FILTER_KEY);
  const [query, setQuery] = useState("");

  const chips = useMemo(
    () => [{ key: ALL_FILTER_KEY, label: "All", predicate: () => true }, ...(filterChips ?? [])],
    [filterChips],
  );
  // `chips` always has at least the "All" entry prepended above, so this
  // fallback is unreachable in practice — the non-null assertion just
  // tells TypeScript what that guarantee already establishes.
  const activeChip = chips.find((chip) => chip.key === activeFilterKey) ?? chips[0]!;

  const filteredRows = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (!activeChip.predicate(row)) return false;
      if (trimmedQuery && getSearchValue) {
        return getSearchValue(row).toLowerCase().includes(trimmedQuery);
      }
      return true;
    });
  }, [rows, activeChip, query, getSearchValue]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    if (sort) {
      const column = columns.find((c) => c.key === sort.key);
      const getSortValue = column?.getSortValue;
      if (getSortValue) {
        copy.sort((a, b) => {
          const av = getSortValue(a);
          const bv = getSortValue(b);
          const cmp =
            typeof av === "number" && typeof bv === "number"
              ? av - bv
              : String(av).localeCompare(String(bv));
          return sort.direction === "ascending" ? cmp : -cmp;
        });
      }
    } else {
      copy.sort(defaultCompare);
    }
    return copy;
  }, [filteredRows, sort, columns, defaultCompare]);

  function handleSort(column: DataTableColumn<Row>) {
    setSort((prev) => {
      if (!prev || prev.key !== column.key) return { key: column.key, direction: "ascending" };
      return {
        key: column.key,
        direction: prev.direction === "ascending" ? "descending" : "ascending",
      };
    });
  }

  const rowCount = sortedRows.length;
  const sortDescription = sort
    ? `${columns.find((c) => c.key === sort.key)?.label ?? sort.key} ${sort.direction}`
    : defaultSortLabel;
  const filterDescription =
    activeFilterKey !== ALL_FILTER_KEY ? `, filtered to ${activeChip.label}` : "";
  const searchDescription = query.trim() ? `, searching "${query.trim()}"` : "";
  const captionText = `${captionBase}, sorted by ${sortDescription}${filterDescription}${searchDescription}, ${rowCount} row${rowCount === 1 ? "" : "s"}.`;

  function handleExport() {
    const csvColumns = columns.map((column) => ({
      label: column.label,
      getValue: column.getCsvValue,
    }));
    const csv = buildCsv(csvColumns, sortedRows);
    downloadCsv(buildCsvFilename(screenSlug), csv);
  }

  return (
    <div class="data-table-wrap">
      <div class="table-toolbar">
        {searchLabel ? (
          <input
            type="search"
            class="table-search"
            aria-label={searchLabel}
            placeholder={searchLabel}
            value={query}
            onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
          />
        ) : (
          <span />
        )}
        {filterChips ? (
          <div class="table-filter-chips" role="group" aria-label={filterGroupLabel}>
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                class="table-filter-chip"
                aria-pressed={activeFilterKey === chip.key}
                onClick={() => setActiveFilterKey(chip.key)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        <button
          type="button"
          class="table-export-btn"
          aria-label={`Export CSV — ${rowCount} row${rowCount === 1 ? "" : "s"} shown`}
          onClick={handleExport}
        >
          ⬇ Export CSV
        </button>
      </div>

      <table class="data-table">
        <caption class="sr-only">{captionText}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const isActive = sort !== null && sort.key === column.key;
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={isActive && sort ? sort.direction : undefined}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      class="data-table-sort-btn"
                      onClick={() => handleSort(column)}
                    >
                      {column.label}
                    </button>
                  ) : (
                    column.label
                  )}
                  {column.headerInfo ?? null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} class="loading-hint">
                {loadingMessage}
              </td>
            </tr>
          ) : rowCount === 0 ? (
            <tr>
              <td colSpan={columns.length} class="empty-hint">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedRows.map((row) => (
              <tr key={getRowId(row)}>
                {columns.map((column) => {
                  const content = column.render ? column.render(row) : column.getCsvValue(row);
                  const isPrimary = column.key === primaryColumnKey;
                  return (
                    <td key={column.key}>
                      {isPrimary && onRowActivate ? (
                        <button
                          type="button"
                          class="data-table-row-btn"
                          aria-label={getRowActivateLabel?.(row)}
                          onClick={() => onRowActivate(row)}
                        >
                          {content}
                        </button>
                      ) : (
                        content
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
